import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit'
import { getDashboard, getOrders } from '../../api/holdallocation/hold-allocation-api'
import { HOLD_ALLOCATION_BUCKETS } from '../../constants/hold-allocation-constants'
import { aggregateBucketBreakdowns } from './utils/aggregate-bucket-breakdowns'

const initialState = {
  dashboard: null,
  orders: [],
  isFetching: false,
  error: null,
  selectedBucket: HOLD_ALLOCATION_BUCKETS.ALL,
  selectedHoldFilter: 'ALL_HOLDS',
  selectedOrder: null,
  searchTerm: '',
}

export const fetchDashboard = createAsyncThunk(
  'holdAllocation/fetchDashboard',
  async ({ role, org } = {}, { rejectWithValue }) => {
    try {
      const res = await getDashboard(role || 'CUSTOMER_SERVICE', org || '')
      return res.data.data
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e.message)
    }
  },
)

export const fetchOrders = createAsyncThunk(
  'holdAllocation/fetchOrders',
  async (params, { rejectWithValue }) => {
    try {
      const res = await getOrders(params)
      return res.data.data
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e.message)
    }
  },
)

const holdAllocationSlice = createSlice({
  name: 'holdAllocation',
  initialState,
  reducers: {
    setSelectedBucket(state, action) {
      state.selectedBucket = action.payload
    },
    setSelectedHoldFilter(state, action) {
      state.selectedHoldFilter = action.payload
    },
    setSelectedOrder(state, action) {
      state.selectedOrder = action.payload
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload
    },
    resetHoldAllocation() {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.isFetching = true
        state.error = null
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.isFetching = false
        state.dashboard = action.payload
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.isFetching = false
        state.error = action.payload
      })
      .addCase(fetchOrders.pending, (state) => {
        state.isFetching = true
        state.error = null
        state.orders = []
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.isFetching = false
        state.orders = action.payload
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.isFetching = false
        state.error = action.payload
      })
  },
})

export const {
  setSelectedBucket,
  setSelectedHoldFilter,
  setSelectedOrder,
  setSearchTerm,
  resetHoldAllocation,
} = holdAllocationSlice.actions

// ─── Selectors ─────────────────────────────────────────────────────
//
// The /dashboard response now embeds a `bucketBreakdowns` map that
// carries the per-bucket chip + hold-code counts, replacing the need
// for a separate /orders/filter-options round-trip. These selectors
// return the breakdown for the currently selected bucket, computing
// an aggregated view on the fly for the `ALL` bucket.

const selectDashboard = (state) => state.holdAllocation.dashboard
const selectSelectedBucket = (state) => state.holdAllocation.selectedBucket

/**
 * Returns the `BucketChipBreakdown` that the chip filter bar should
 * render for the currently selected bucket, or `null` if the
 * dashboard has not loaded yet or does not contain breakdowns.
 *
 * - For a specific bucket (RELEASABLE/LOCKED/READY_TO_ALLOCATE/
 *   AWAITING_STOCK), returns `dashboard.bucketBreakdowns[<bucket>]`.
 * - For the `ALL` bucket, returns a client-side aggregation built
 *   from every entry in `dashboard.bucketBreakdowns`.
 */
export const selectBucketBreakdownForSelected = createSelector(
  [selectDashboard, selectSelectedBucket],
  (dashboard, selectedBucket) => {
    const breakdowns = dashboard?.bucketBreakdowns
    if (!breakdowns) return null
    if (selectedBucket === HOLD_ALLOCATION_BUCKETS.ALL) {
      return aggregateBucketBreakdowns(breakdowns)
    }
    return breakdowns[selectedBucket] || null
  },
)

export default holdAllocationSlice.reducer
